import { apiRequest } from "./apiClient";
import { stripHtml } from "../utils/richText";

const whatsappNumber = (process.env.REACT_APP_WHATSAPP_NUMBER || "918700338552").replace(/\D/g, "");

function money(value) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function cleanLine(label, value) {
  const clean = stripHtml(value || "").trim();
  if (!clean || clean.toLowerCase() === "n/a") return null;
  return `${label}: ${clean}`;
}

function buildCleanWhatsAppUrl(enquiry, selectedProducts = []) {
  const customer = enquiry?.customer || {};
  const quote = enquiry?.quote || {};
  const quoteItems = Array.isArray(quote.items) ? quote.items : [];
  const sourceItems = quoteItems.length ? quoteItems : selectedProducts;
  const number = (enquiry?.whatsapp?.number || whatsappNumber).replace(/\D/g, "");

  if (!number) return "";

  const customerLines = [
    cleanLine("Name", customer.name),
    cleanLine("Company", customer.companyName),
    cleanLine("Phone", customer.phone),
    cleanLine("Email", customer.email),
    cleanLine("GST", customer.gstNumber),
    cleanLine("Address", customer.address),
  ].filter(Boolean);

  const productLines = sourceItems.length
    ? sourceItems.map((item, index) => {
        const quantity = Number(item.quantity || item.minQty || 1);
        const unit = item.unit || item.selectedVariant?.unit || "pcs";
        const unitPrice = Number(item.unitPrice ?? item.finalPrice ?? item.priceWithGst ?? item.price ?? 0);
        const amount = Number(item.lineTotal ?? quantity * unitPrice);
        return [
          `${index + 1}. ${stripHtml(item.name || "Product")}`,
          item.productId || item.id ? `   ID: ${item.productId || item.id}` : null,
          `   Qty: ${quantity} ${unit}`,
          `   Rate: ${money(unitPrice)}`,
          `   Amount: ${money(amount)}`,
        ].filter(Boolean).join("\n");
      })
    : ["No products selected"];

  const subtotal = Number(quote.subtotal ?? sourceItems.reduce((sum, item) => {
    const quantity = Number(item.quantity || item.minQty || 1);
    const unitPrice = Number(item.unitPrice ?? item.finalPrice ?? item.priceWithGst ?? item.price ?? 0);
    return sum + Number(item.lineTotal ?? quantity * unitPrice);
  }, 0));
  const gst = Number(quote.gst ?? Math.round(subtotal * 0.18));
  const total = Number(quote.total ?? subtotal + gst);
  const totalQuantity = Number(quote.totalQuantity ?? sourceItems.reduce((sum, item) => sum + Number(item.quantity || item.minQty || 1), 0));

  const message = [
    "*MOZO EV - QUOTATION REQUEST*",
    `Enquiry ID: ${enquiry?.enquiryId || `ENQ-${Date.now()}`}`,
    `Date: ${new Date(enquiry?.createdAt || Date.now()).toLocaleString("en-IN")}`,
    "",
    "*Customer Details*",
    ...(customerLines.length ? customerLines : ["No customer details added"]),
    "",
    "*Quoted Products*",
    ...productLines,
    "",
    "*Summary*",
    `Total Quantity: ${totalQuantity}`,
    `Subtotal: ${money(subtotal)}`,
    `GST: ${money(gst)}`,
    `Grand Total: ${money(total)}`,
    "",
    "Final rate and availability will be confirmed by Mozo sales team.",
  ].join("\n");

  return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
}

export const submitQuoteRequest = async (payload) => {
  const selectedProducts = payload.selectedProducts || [];
  const items = selectedProducts.map((item) => ({
    productId: item.id,
    variantLabel: item.selectedVariant?.label || "",
    quantity: Number(item.quantity || item.minQty || 1),
  }));

  const customer = {
    companyName: payload.companyName,
    name: payload.customerName,
    email: payload.email,
    phone: payload.phone,
    gstNumber: payload.gstNumber,
    address: payload.address,
  };

  const data = await apiRequest("/enquiries", {
    method: "POST",
    body: JSON.stringify({ customer, items }),
  });

  const enquiry = data.enquiry || {
    enquiryId: `ENQ-${Date.now()}`,
    createdAt: new Date().toISOString(),
    customer,
    quote: { items: selectedProducts },
  };

  return {
    ok: true,
    referenceId: enquiry.enquiryId,
    enquiry,
    whatsappUrl: buildCleanWhatsAppUrl(enquiry, selectedProducts),
  };
};
